import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MDBBtn, MDBIcon } from 'mdbreact';

import {
  paraDataUrl,
  ehPdf,
  dataUrlParaBlob,
  proximoZoom,
  ZOOM_MAX,
} from './arquivoCnh';

// Prepara o anexo da CNH para exibição (issue #324).
//
// É hook, e não estado interno do componente, porque a ação "abrir em nova
// aba" pertence à barra de ações de quem chama — no Perfil, o rodapé do modal.
// Botão flutuando sobre o documento, com outro botão no rodapé, espalhava
// ação em dois lugares e comia altura útil do documento.
export function useArquivoCnh(valor) {
  const dataUrl = useMemo(() => paraDataUrl(valor), [valor]);
  const pdf = ehPdf(dataUrl);

  // O <iframe> aceitaria a data URL, mas ela viaja como string de megabytes no
  // atributo; o Blob deixa isso fora do DOM e é o que faz o "abrir em nova
  // aba" funcionar — navegar para `data:` é bloqueado pelo Chrome.
  const urlBlob = useMemo(() => {
    if (!dataUrl || !pdf) return '';
    return URL.createObjectURL(dataUrlParaBlob(dataUrl));
  }, [dataUrl, pdf]);

  useEffect(
    () => () => {
      if (urlBlob) URL.revokeObjectURL(urlBlob);
    },
    [urlBlob]
  );

  return {
    dataUrl,
    pdf,
    urlBlob,
    temArquivo: !!dataUrl,
    abrirEmNovaAba: () => window.open(urlBlob, '_blank', 'noopener'),
  };
}

// Botão da ação, para o rodapé do modal de quem chama. Só aparece com PDF:
// imagem já é exibida inteira e ampliável ali mesmo.
export function BotaoAbrirCnh({ cnh }) {
  if (!cnh.temArquivo || !cnh.pdf) return null;

  return (
    <MDBBtn color="dark-green" onClick={cnh.abrirEmNovaAba}>
      <MDBIcon far icon="file-pdf" className="mr-2" />
      Abrir em nova aba
    </MDBBtn>
  );
}

// Visualizador do anexo — imagem ou PDF.
//
// NÃO abre modal próprio: quem chama já está dentro de um (o Perfil abre
// "Última CNH cadastrada", o Editar abre "Arquivo da CNH"). Ter modal aqui
// empilhava dois para mostrar um documento só.
//
// Segue o padrão que o SGO já usa (SendImg/#31: PDF em <iframe>, imagem com
// zoom), com duas diferenças que vieram de olhar o que estava quebrado lá:
//
//   1. o zoom da imagem muda a LARGURA, não `transform: scale()`. Com
//      transform o elemento continua do tamanho antigo para o container, e o
//      scroll não acompanha: ampliar sem poder rolar até a metade de baixo do
//      documento é o que faz a CNH "ficar ruim de ler".
//   2. arrastar move a imagem ampliada — no celular o app desliga o pinch
//      (user-scalable=no no index.html), então sem pan não há como percorrer
//      o documento ampliado.
function ArquivoCnh({ cnh, altura = '70vh' }) {
  const [zoom, setZoom] = useState(1);

  const areaRef = useRef(null);
  const arrasto = useRef(null);

  const iniciaArrasto = evento => {
    if (!areaRef.current || zoom === 1) return;
    arrasto.current = {
      x: evento.clientX,
      y: evento.clientY,
      left: areaRef.current.scrollLeft,
      top: areaRef.current.scrollTop,
    };
  };

  const move = evento => {
    if (!arrasto.current || !areaRef.current) return;
    areaRef.current.scrollLeft =
      arrasto.current.left - (evento.clientX - arrasto.current.x);
    areaRef.current.scrollTop =
      arrasto.current.top - (evento.clientY - arrasto.current.y);
  };

  const soltaArrasto = () => {
    arrasto.current = null;
  };

  if (!cnh.temArquivo) return null;

  if (cnh.pdf) {
    // Altura em vh para caber na tela do celular sem duplo scroll. Em iOS o
    // PDF não renderiza em iframe — a saída é o botão do rodapé.
    return (
      <iframe
        title="CNH em PDF"
        src={cnh.urlBlob}
        style={{ width: '100%', height: altura, border: 'none' }}
      />
    );
  }

  return (
    <div className="w-100">
      <div
        ref={areaRef}
        onMouseDown={iniciaArrasto}
        onMouseMove={move}
        onMouseUp={soltaArrasto}
        onMouseLeave={soltaArrasto}
        onDoubleClick={() => setZoom(zoom > 1 ? 1 : 2)}
        style={{
          overflow: 'auto',
          maxHeight: altura,
          cursor: zoom > 1 ? 'grab' : 'zoom-in',
          WebkitOverflowScrolling: 'touch',
          // Permite arrastar e pinçar, mas desliga o duplo-toque-para-zoom do
          // navegador, que brigaria com o onDoubleClick daqui no dia em que o
          // app parar de mandar user-scalable=no.
          touchAction: 'manipulation',
        }}
      >
        <img
          src={cnh.dataUrl}
          alt="CNH"
          draggable={false}
          style={{
            width: `${zoom * 100}%`,
            maxWidth: 'none',
            display: 'block',
          }}
        />
      </div>

      {/* Botões em tamanho cheio: no celular este é o único jeito de ampliar,
          porque o app desliga o pinch. */}
      <div className="d-flex justify-content-center align-items-center mt-2">
        <MDBBtn
          color="dark-green"
          disabled={zoom >= ZOOM_MAX}
          onClick={() => setZoom(proximoZoom(zoom, 1.5))}
          title="Aproximar"
        >
          <MDBIcon icon="search-plus" />
        </MDBBtn>
        <MDBBtn
          color="dark-green"
          disabled={zoom <= 1}
          onClick={() => setZoom(proximoZoom(zoom, 1 / 1.5))}
          title="Afastar"
        >
          <MDBIcon icon="search-minus" />
        </MDBBtn>
        <MDBBtn
          color="dark-green"
          disabled={zoom === 1}
          onClick={() => setZoom(1)}
          title="Tamanho da tela"
        >
          <MDBIcon fas icon="compress" />
        </MDBBtn>
        <span className="ml-2 font-weight-bold">{Math.round(zoom * 100)}%</span>
      </div>
    </div>
  );
}

export default ArquivoCnh;
